import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useCallback, useMemo } from "react";
import { validateRouteRegistry, type ValidationResult } from "@/lib/route-validator";

export interface RouteRecord {
  id: string;
  parent_route_id: string | null;
  route_name: string | null;
  path: string;
  component: string | null;
  component_props: Record<string, unknown>;
  layout: string | null;
  domain: string;
  route_mode: string;
  status: string;
  auth_required: boolean;
  redirect_to: string | null;
  notes: string | null;
  sort_order: number;
  is_system: boolean;
  is_locked: boolean;
  metadata: Record<string, unknown>;
  updated_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface RouteChangeRecord {
  id: string;
  route_id: string | null;
  changed_by: string | null;
  change_type: string;
  changed_fields: string[] | null;
  previous_values: Record<string, unknown> | null;
  new_values: Record<string, unknown> | null;
  change_summary: string | null;
  source: string | null;
  route_path_snapshot: string | null;
  created_at: string;
}

const ROUTE_REGISTRY_KEY = ["route_registry"];
const ROUTE_CHANGE_LOG_KEY = ["route_change_log"];

export function useRouteRegistry() {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ROUTE_REGISTRY_KEY,
    queryFn: async (): Promise<RouteRecord[]> => {
      const { data, error } = await supabase
        .from("route_registry")
        .select("*")
        .order("sort_order", { ascending: true })
        .order("domain", { ascending: true })
        .order("path", { ascending: true });

      if (error) throw error;
      return (data ?? []) as unknown as RouteRecord[];
    },
    staleTime: 30_000,
  });

  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ROUTE_REGISTRY_KEY });
  }, [queryClient]);

  /** Validation result computed from current route data */
  const validation: ValidationResult | null = useMemo(() => {
    if (!query.data || query.data.length === 0) return null;
    return validateRouteRegistry(query.data);
  }, [query.data]);

  return {
    routes: query.data ?? [],
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
    invalidate,
    lastUpdated: query.dataUpdatedAt,
    validation,
  };
}

export function useRouteChangeLog(filters?: {
  changeType?: string;
  pathSearch?: string;
  dateFrom?: string;
  dateTo?: string;
}) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: [...ROUTE_CHANGE_LOG_KEY, filters],
    queryFn: async (): Promise<RouteChangeRecord[]> => {
      let q = supabase
        .from("route_change_log")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(500);

      if (filters?.changeType && filters.changeType !== "All") {
        q = q.eq("change_type", filters.changeType);
      }
      if (filters?.pathSearch) {
        q = q.ilike("route_path_snapshot", `%${filters.pathSearch}%`);
      }
      if (filters?.dateFrom) {
        q = q.gte("created_at", filters.dateFrom);
      }
      if (filters?.dateTo) {
        q = q.lte("created_at", `${filters.dateTo}T23:59:59`);
      }

      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as unknown as RouteChangeRecord[];
    },
    staleTime: 30_000,
  });

  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ROUTE_CHANGE_LOG_KEY });
  }, [queryClient]);

  return {
    logs: query.data ?? [],
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
    invalidate,
  };
}
